// VC Portfolio Intelligence Onboarding Page
// Flow: Landing sell → CTA click → Scan + Signup in parallel

const { useState, useEffect, useRef } = React;

// ==================== Config & Mock Data ====================

const {
  firm_name, firm_domain, contact_name, spots_remaining, portfolio_found,
  portfolio_companies: PRELOADED_PORTFOLIO, vc_token, is_authenticated,
  token_already_used,
} = window.VC_DATA || {};
const regConfig = window.REGISTRATION_CONFIG || {
  recaptchaSiteKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
  termsOfUseUrl: '/terms'
};

const HAS_TOKEN = vc_token && vc_token.length > 0;
const HAS_PRELOADED = Array.isArray(PRELOADED_PORTFOLIO) && PRELOADED_PORTFOLIO.length > 0;

const CATEGORY_CONFIG = {
  'blog': { dotColor: 'bg-green-500', badgeBg: 'bg-green-100', badgeText: 'text-green-700', label: 'blog' },
  'x': { dotColor: 'bg-sky-500', badgeBg: 'bg-sky-100', badgeText: 'text-sky-700', label: 'x post' },
  'website update': { dotColor: 'bg-amber-500', badgeBg: 'bg-amber-100', badgeText: 'text-amber-700', label: 'website' },
};

const MOCK_TIMELINE = [
  {
    id: 1, category: "blog", creator: "Stripe", creator_domain: "stripe.com",
    title: "Stripe launches AI-powered fraud prevention for global payments",
    description: "New ML model reduces false positives by 40% across payment flows, now available for all Stripe users.",
    time_label: "2h ago",
  },
  {
    id: 2, category: "x", creator: "Linear", creator_domain: "linear.app",
    title: "We just shipped Cycles 2.0 with auto-scheduling. Teams can now let Linear auto-assign issues based on capacity.",
    description: "",
    time_label: "5h ago",
  },
  {
    id: 3, category: "website update", creator: "Notion", creator_domain: "notion.so",
    title: "Pricing page updated - new Enterprise tier with SSO and audit logs",
    description: "Notion's pricing page now features a dedicated Enterprise plan starting at $25/user/month with advanced compliance features.",
    time_label: "Yesterday",
  },
  {
    id: 4, category: "blog", creator: "Vercel", creator_domain: "vercel.com",
    title: "Vercel announces edge middleware GA with 99.99% SLA",
    description: "Edge Middleware is now generally available. Deploy serverless functions at the edge with guaranteed uptime.",
    time_label: "Yesterday",
  },
];

// ==================== Utility: domain parsing ====================

function parseDomain(input) {
  let val = input.trim().toLowerCase();
  val = val.replace(/^https?:\/\//, '');
  val = val.replace(/^www\./, '');
  val = val.split('/')[0];
  return val;
}

function isValidDomain(d) {
  return d.length > 2 && d.includes('.') && !d.includes(' ');
}

function domainToName(d) {
  const base = d.split('.')[0];
  return base.charAt(0).toUpperCase() + base.slice(1);
}

// ==================== Timeline Item ====================

const TimelineItem = ({ item }) => {
  const cat = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG['blog'];
  return (
    <div className="relative flex items-start gap-4 pl-1 mb-6">
      <div className="w-8 flex items-center justify-center relative z-10 mt-1">
        <div className={`w-2.5 h-2.5 rounded-full ${cat.dotColor} ring-4 ring-white`}></div>
      </div>
      <div className="flex-1 bg-white rounded-xl border border-zinc-200 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <img
            src={`https://www.google.com/s2/favicons?domain=${item.creator_domain}&sz=32`}
            className="w-5 h-5 rounded object-contain"
            alt={item.creator}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <span className="text-sm font-semibold text-zinc-900">{item.creator}</span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cat.badgeBg} ${cat.badgeText}`}>{cat.label}</span>
          <span className="text-xs text-zinc-400 ml-auto">{item.time_label}</span>
        </div>
        <p className="text-sm font-medium text-zinc-800">{item.title}</p>
        {item.description && (
          <p className="text-sm text-zinc-600 mt-1 line-clamp-2">{item.description}</p>
        )}
      </div>
    </div>
  );
};

// ==================== Timeline Demo in Browser Chrome ====================

const RSS_MOCK_XML = `<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0">
  <channel>
    <title>${firm_name}'s Portfolio Feed</title>
    <link>https://ninjapear.co</link>
    <description>Portfolio company updates</description>
    <item>
      <title>Stripe launches AI-powered fraud
        prevention for global payments</title>
      <category>blog</category>
      <pubDate>2h ago</pubDate>
    </item>
    <item>
      <title>Linear ships Cycles 2.0 with
        auto-scheduling</title>
      <category>x</category>
      <pubDate>5h ago</pubDate>
    </item>
    <item>
      <title>Notion pricing page updated -
        new Enterprise tier</title>
      <category>website</category>
      <pubDate>Yesterday</pubDate>
    </item>
  </channel>
</rss>`;

const TimelineDemoFrame = () => {
  const [view, setView] = useState('timeline');
  const demoItems = MOCK_TIMELINE.slice(0, 3);
  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl border border-zinc-200 shadow-xl overflow-hidden">
        {/* Browser chrome bar */}
        <div className="flex items-center gap-3 px-5 py-3 bg-zinc-100 border-b border-zinc-200">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-400"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
            <div className="w-3 h-3 rounded-full bg-green-400"></div>
          </div>
          <div className="flex-1 text-center">
            <span className="text-sm text-zinc-500 font-medium">{firm_name}&apos;s Portfolio Feed</span>
          </div>
        </div>

        {/* View toggle */}
        <div className="flex items-center gap-1 px-5 pt-4 pb-2 bg-zinc-50">
          <button
            onClick={() => setView('timeline')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              view === 'timeline'
                ? 'bg-purple-100 text-purple-700'
                : 'text-zinc-500 hover:bg-zinc-100'
            }`}
          >
            <i className="ph ph-list-bullets"></i>Timeline
          </button>
          <button
            onClick={() => setView('rss')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              view === 'rss'
                ? 'bg-orange-100 text-orange-700'
                : 'text-zinc-500 hover:bg-zinc-100'
            }`}
          >
            <i className="ph ph-rss"></i>RSS Feed
          </button>
        </div>

        {view === 'timeline' ? (
          <div className="p-6 pt-3 bg-zinc-50">
            <div className="relative">
              {/* Timeline spine */}
              <div className="absolute left-5 top-0 bottom-0 w-px bg-zinc-200"></div>

              {/* Today label */}
              <div className="relative flex items-center gap-4 mb-6">
                <div className="w-10 h-6 flex items-center justify-center relative z-10">
                  <span className="text-xs font-semibold text-purple-600 bg-purple-50 border border-purple-200 rounded-full px-2.5 py-0.5">Today</span>
                </div>
              </div>

              {/* Demo items */}
              {demoItems.map(item => (
                <TimelineItem key={item.id} item={item} />
              ))}

              {/* Fade-out hint */}
              <div className="h-12 bg-gradient-to-b from-transparent to-zinc-50"></div>
            </div>
          </div>
        ) : (
          <div className="p-5 pt-3 bg-zinc-50">
            <pre className="bg-zinc-900 text-zinc-100 rounded-xl p-4 text-xs leading-relaxed overflow-x-auto font-mono"><code>{RSS_MOCK_XML}</code></pre>
            <p className="text-xs text-zinc-400 mt-3 text-center flex items-center justify-center gap-1.5">
              <i className="ph ph-rss text-orange-400"></i>
              Paste this URL into Slack, Feedly, Zapier, or any RSS reader
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== Value Props Row ====================

const VALUE_PROPS = [
  {
    icon: 'ph-fill ph-article',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    title: 'Blog Posts',
    description: 'Detect new articles from any company blog',
  },
  {
    icon: 'ph-fill ph-x-logo',
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
    title: 'X / Twitter',
    description: 'Surface relevant tweets from company accounts',
  },
  {
    icon: 'ph-fill ph-globe',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    title: 'Website Changes',
    description: 'AI-filtered diffs on pricing, team, and product pages',
  },
];

const ValuePropsRow = () => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
    {VALUE_PROPS.map((prop, i) => (
      <div key={i} className="bg-white rounded-xl border border-zinc-200 p-5 text-center">
        <div className={`w-10 h-10 rounded-full ${prop.iconBg} flex items-center justify-center mx-auto mb-3`}>
          <i className={`${prop.icon} text-lg ${prop.iconColor}`}></i>
        </div>
        <h4 className="text-sm font-bold text-zinc-900 mb-1">{prop.title}</h4>
        <p className="text-xs text-zinc-500 leading-relaxed">{prop.description}</p>
      </div>
    ))}
  </div>
);

// ==================== Landing Sell Section (Step 1) ====================

const PersonalizedGreeting = () => {
  const hasContact = contact_name && contact_name.length > 0;
  if (!HAS_TOKEN || !firm_domain || firm_domain === 'example.com') return null;

  return (
    <div className="max-w-2xl mx-auto mb-10 bg-white rounded-2xl border border-zinc-200 shadow-sm p-6 sm:p-8">
      <div className="flex items-start gap-4 mb-4">
        <img
          src={`https://www.google.com/s2/favicons?domain=${firm_domain}&sz=64`}
          className="w-14 h-14 rounded-xl border border-zinc-200 bg-white object-contain flex-shrink-0"
          alt={firm_name}
        />
        <div>
          <h2 className="text-lg font-bold text-zinc-900">
            {hasContact ? `Hi ${contact_name}` : `Hi ${firm_name} team`} &#128075;
          </h2>
          <p className="text-sm text-zinc-600 mt-1.5 leading-relaxed">
            Thank you for being a Proxycurl customer &mdash; it means a lot.
            I&apos;m building <span className="font-semibold text-zinc-800">NinjaPear</span>, my new competitive intelligence data product,
            and I&apos;d love for you to be an early tester for the company monitoring feature we just shipped.
          </p>
          <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
            It tracks your portfolio companies&apos; blog posts, X activity, and website changes in a single RSS feed &mdash;
            so you always know what&apos;s happening across your investments without checking 50 tabs.
          </p>
        </div>
      </div>
    </div>
  );
};

const LandingSellSection = ({ onActivate, onActivateWithEmail }) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [captchaSolved, setCaptchaSolved] = useState(false);
  const landingCaptchaRendered = useRef(false);

  // Render captcha for no-token flow
  useEffect(() => {
    if (HAS_TOKEN) return;
    function renderCaptcha() {
      const container = document.getElementById('landing-captcha-container');
      if (container && typeof grecaptcha !== 'undefined' && !landingCaptchaRendered.current) {
        landingCaptchaRendered.current = true;
        grecaptcha.render('landing-captcha-container', {
          sitekey: regConfig.recaptchaSiteKey,
          callback: () => setCaptchaSolved(true),
          'expired-callback': () => setCaptchaSolved(false),
        });
      }
    }
    window.onRecaptchaLoadReact = renderCaptcha;
    if (window.recaptchaReady) renderCaptcha();
    return () => { window.onRecaptchaLoadReact = null; };
  }, []);

  const handleNoTokenActivate = () => {
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes('@')) {
      setEmailError('Please enter a valid work email');
      return;
    }
    const domain = trimmed.split('@')[1];
    if (!domain || domain.length < 3 || !domain.includes('.')) {
      setEmailError('Please enter a valid work email');
      return;
    }
    if (!captchaSolved) {
      setEmailError('Please complete the captcha');
      return;
    }
    setEmailError('');
    // Get captcha response token to pass to backend
    const captchaResponse = typeof grecaptcha !== 'undefined' ? grecaptcha.getResponse() : '';
    onActivateWithEmail(trimmed, domain, captchaResponse);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-16">
      {/* Personalized greeting */}
      <PersonalizedGreeting />

      {/* Hero */}
      <div className="text-center mb-10">
        <h1 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-3 leading-tight">
          Track your portfolio companies in one feed
        </h1>
        <p className="text-lg text-zinc-500 max-w-xl mx-auto">
          Monitor blog posts, website changes, and X activity across your entire portfolio &mdash; delivered as an RSS feed to Slack, Feedly, or any reader.
        </p>
      </div>

      {/* Timeline demo in browser chrome */}
      <div className="mb-12">
        <TimelineDemoFrame />
      </div>

      {/* Value props */}
      <div className="mb-12">
        <ValuePropsRow />
      </div>

      {/* Urgency + CTA */}
      <div className="text-center">
        {HAS_TOKEN && (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium mb-6">
            <i className="ph-fill ph-fire text-amber-600"></i>
            Free for the first 10 VC funds &mdash; {spots_remaining} spots remaining
          </div>
        )}

        {HAS_TOKEN ? (
          /* Token flow: simple CTA button */
          <div>
            <button
              onClick={onActivate}
              className="inline-flex items-center justify-center px-10 py-4 text-lg font-semibold text-white bg-purple-600 rounded-xl shadow-lg hover:bg-purple-700 hover:shadow-xl transition-all cursor-pointer"
            >
              Scan My Portfolio
              <i className="ph-bold ph-arrow-right ml-2"></i>
            </button>
          </div>
        ) : (
          /* No-token flow: email input + CTA */
          <div className="max-w-md mx-auto">
            <div className="flex items-center gap-2">
              <input
                type="email"
                className="flex-1 px-4 py-3.5 text-sm rounded-xl border border-zinc-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder:text-zinc-400"
                placeholder="Enter your work email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleNoTokenActivate(); }}}
              />
              <button
                onClick={handleNoTokenActivate}
                className="inline-flex items-center justify-center px-6 py-3.5 text-sm font-semibold text-white bg-purple-600 rounded-xl shadow-lg hover:bg-purple-700 hover:shadow-xl transition-all cursor-pointer flex-shrink-0"
              >
                Scan Portfolio
                <i className="ph-bold ph-arrow-right ml-2"></i>
              </button>
            </div>
            {emailError && <p className="text-xs text-red-500 mt-2">{emailError}</p>}
            <div id="landing-captcha-container" className="mt-3 flex justify-center"></div>
          </div>
        )}

        {HAS_TOKEN && (
          <p className="text-sm text-zinc-400 mt-4">
            <span className="font-medium text-zinc-600">{10 - spots_remaining} funds already monitoring</span>
            <span className="mx-2 text-zinc-300">&middot;</span>
            No credit card required
          </p>
        )}
      </div>
    </div>
  );
};

// ==================== Scanning Step (inline, card-sized) ====================

const ScanningStep = ({ onComplete, portfolioCount }) => {
  const [logs, setLogs] = useState([]);
  const count = portfolioCount || 0;
  const lastMsg = count > 0
    ? `${count} portfolio companies found`
    : "Portfolio scan complete";

  const LOG_MESSAGES = [
    "Connecting to company graph...",
    "Identifying portfolio investments...",
    "Enriching company profiles...",
    lastMsg,
  ];

  useEffect(() => {
    const timers = LOG_MESSAGES.map((msg, i) =>
      setTimeout(() => {
        setLogs(prev => [...prev, msg]);
        if (i === LOG_MESSAGES.length - 1) {
          setTimeout(onComplete, 600);
        }
      }, 600 + i * 700)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      {/* Pulsing radar */}
      <div className="relative w-20 h-20 mb-6">
        <div className="absolute inset-0 rounded-full bg-purple-100 animate-ping opacity-30"></div>
        <div className="absolute inset-2 rounded-full bg-purple-50 animate-pulse"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <img
            src={`https://www.google.com/s2/favicons?domain=${firm_domain}&sz=64`}
            className="w-10 h-10 rounded-xl shadow-sm bg-white p-1"
            alt={firm_name}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      </div>

      <h3 className="text-lg font-bold text-zinc-900 mb-1 text-center">
        Scanning <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">{firm_name}</span>&apos;s portfolio&hellip;
      </h3>
      <p className="text-zinc-500 text-xs mb-6">Analyzing company graph and enriching data</p>

      {/* Scan log */}
      <div className="w-full max-w-xs space-y-2.5">
        {LOG_MESSAGES.map((msg, i) => {
          const revealed = logs.includes(msg);
          const isLast = i === LOG_MESSAGES.length - 1;
          const isSuccess = isLast && count > 0;
          return (
            <div
              key={i}
              className={`flex items-center gap-2.5 text-sm transition-all duration-300 ${
                revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
              }`}
            >
              {revealed
                ? <i className={`ph-fill ph-check-circle flex-shrink-0 ${isSuccess ? 'text-green-500' : 'text-zinc-400'}`}></i>
                : <i className="ph ph-circle-dashed text-zinc-300 flex-shrink-0"></i>
              }
              <span className={isLast && revealed ? (isSuccess ? 'font-semibold text-green-700' : 'font-medium text-zinc-700') : 'text-zinc-600'}>
                {msg}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==================== Urgency Banner ====================

const UrgencyBanner = ({ spotsRemaining }) => {
  const taken = 10 - spotsRemaining;
  return (
    <div className="mb-6 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium flex items-center gap-2 animate-reveal">
      <i className="ph-fill ph-fire text-amber-600 text-base"></i>
      <span>Only {spotsRemaining} of 10 spots remaining &mdash; {taken} VC funds already tracking their portfolio</span>
    </div>
  );
};

// ==================== Company Card ====================

const CompanyCard = ({ company, selected, onToggle, onRemove, revealIndex, reachStatus }) => (
  <div
    className={`group relative cursor-pointer rounded-xl border p-3 transition-all duration-200 animate-reveal
      ${selected ? 'border-purple-400 bg-purple-50 shadow-sm' : 'border-zinc-200 bg-white hover:border-zinc-300'}
      ${reachStatus === 'checking' ? 'opacity-70' : ''}
    `}
    style={{ animationDelay: `${revealIndex * 60}ms` }}
    onClick={() => onToggle(company.domain)}
  >
    <div className="flex items-center gap-2">
      {reachStatus === 'checking' ? (
        <i className="ph ph-spinner animate-spin text-zinc-400 w-5 h-5 flex items-center justify-center flex-shrink-0" style={{ fontSize: '14px' }}></i>
      ) : (
        <img
          src={`https://www.google.com/s2/favicons?domain=${company.domain}&sz=32`}
          className="w-5 h-5 rounded flex-shrink-0"
          alt={company.name}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      )}
      <span className="text-sm font-medium text-zinc-800 flex-1 min-w-0 break-words leading-tight">{company.name}</span>

      {/* Remove button */}
      <button
        className="w-4 h-4 rounded flex items-center justify-center text-zinc-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
        onClick={(e) => { e.stopPropagation(); onRemove(company.domain); }}
        title="Remove company"
      >
        <i className="ph-bold ph-x" style={{ fontSize: '9px' }}></i>
      </button>

      {/* Checkbox */}
      <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors flex-shrink-0
        ${selected ? 'bg-purple-600 border-purple-600' : 'border-zinc-300 bg-white'}`}>
        {selected && <i className="ph-bold ph-check text-white" style={{ fontSize: '9px' }}></i>}
      </div>
    </div>
  </div>
);

// ==================== Add Company Input ====================

const AddCompanyInput = ({ onAdd }) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  const handleAdd = async () => {
    const domain = parseDomain(value);
    if (!domain) return;
    if (!isValidDomain(domain)) {
      setError('Enter a valid domain (e.g. stripe.com)');
      return;
    }
    setChecking(true);
    setError('');
    try {
      const result = await onAdd(domain);
      if (result === 'duplicate') {
        setError('This company is already in the list');
      } else if (result === 'unreachable') {
        setError('This website is not reachable. Did you include the full domain? e.g. "www.revolut.com" instead of "revolut.com"');
      } else {
        setValue('');
        setError('');
      }
    } finally {
      setChecking(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="mt-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <i className={`ph ${checking ? 'ph-spinner animate-spin' : 'ph-plus-circle'} absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400`}></i>
          <input
            type="text"
            className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-zinc-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder:text-zinc-400"
            placeholder="Add a company by URL (e.g. stripe.com)"
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(''); }}
            onKeyDown={handleKeyDown}
            disabled={checking}
          />
        </div>
        <button
          onClick={handleAdd}
          disabled={checking}
          className={`px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-colors flex-shrink-0
            ${checking ? 'bg-zinc-300 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'}`}
        >
          {checking ? 'Checking...' : 'Add'}
        </button>
      </div>
      {error && <p className="text-xs text-red-500 mt-1.5 ml-1">{error}</p>}
    </div>
  );
};

// ==================== Portfolio Grid ====================

const MAX_TARGETS = 100;

const PortfolioGrid = ({ companies, selected, onToggle, onRemove, onAdd, onSelectAll, onDeselectAll, reachabilityStatus }) => {
  const isEmpty = companies.length === 0;
  const atLimit = companies.length >= MAX_TARGETS;

  return (
    <div>
      {isEmpty && (
        <div className="text-center py-8 px-4 bg-white rounded-xl border border-zinc-200 mb-4 animate-reveal">
          <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-3">
            <i className="ph ph-magnifying-glass text-2xl text-zinc-400"></i>
          </div>
          <p className="text-sm font-semibold text-zinc-700 mb-1">
            We couldn&apos;t automatically detect {firm_name}&apos;s portfolio
          </p>
          <p className="text-xs text-zinc-500">Add the companies you&apos;d like to track below</p>
        </div>
      )}

      {!isEmpty && (
        <>
          <div className="grid grid-cols-2 gap-3">
            {companies.map((company, idx) => (
              <CompanyCard
                key={company.domain}
                company={company}
                selected={selected.has(company.domain)}
                onToggle={onToggle}
                onRemove={onRemove}
                revealIndex={idx}
                reachStatus={reachabilityStatus ? reachabilityStatus[company.domain] : undefined}
              />
            ))}
          </div>

          {/* Selection controls */}
          <div className="flex items-center gap-3 mt-3 text-xs text-zinc-500">
            <span className="font-medium text-zinc-700">{selected.size} selected</span>
            <span className="text-zinc-300">&middot;</span>
            <button onClick={onSelectAll} className="hover:text-purple-600 transition-colors underline">Select all</button>
            <button onClick={onDeselectAll} className="hover:text-purple-600 transition-colors underline">Deselect all</button>
          </div>
        </>
      )}

      {atLimit ? (
        <p className="text-xs text-amber-600 mt-4 flex items-center gap-1.5">
          <i className="ph-fill ph-warning-circle"></i>
          Maximum of {MAX_TARGETS} companies reached
        </p>
      ) : (
        <AddCompanyInput onAdd={onAdd} />
      )}
    </div>
  );
};

// ==================== Inline Signup Form ====================

const InlineSignupForm = ({ selectedCompanies }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    termsAgree: false,
  });
  const [captchaState, setCaptchaState] = useState({ isLoading: true, isSolved: false });
  const [domainValidation, setDomainValidation] = useState({
    isValidating: false, isValid: null, reason: null, domain: null
  });
  const formRef = useRef(null);
  const captchaRendered = useRef(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

    if (name === 'email') {
      validateDomainFromEmail(value);
    }
  };

  const validateDomainFromEmail = (email) => {
    setDomainValidation({ isValidating: false, isValid: null, reason: null, domain: null });
    if (!email || !email.includes('@')) return;
    const domain = email.split('@')[1];
    if (!domain || domain.length < 3) return;

    setDomainValidation(prev => ({ ...prev, isValidating: true, domain }));

    if (window._vcDomainTimeout) clearTimeout(window._vcDomainTimeout);
    window._vcDomainTimeout = setTimeout(async () => {
      try {
        const res = await fetch('/auth/api/validate-domain', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ domain })
        });
        const result = await res.json();
        setDomainValidation({
          isValidating: false,
          isValid: res.ok ? result.accessible : false,
          reason: result.reason || result.error || '',
          domain
        });
      } catch {
        setDomainValidation({ isValidating: false, isValid: null, reason: 'Unable to validate', domain });
      }
    }, 1500);
  };

  useEffect(() => {
    if (typeof $ !== 'undefined' && typeof $.fn.parsley !== 'undefined') {
      const form = document.getElementById('vc-signup-form');
      if (form) {
        $(form).parsley({
          errorClass: 'is-invalid',
          successClass: 'is-valid',
          errorsWrapper: '<div class="text-xs text-red-500 mt-1"></div>',
          errorTemplate: '<span></span>'
        });
      }
    }
  }, []);

  useEffect(() => {
    function renderCaptcha() {
      setCaptchaState(prev => ({ ...prev, isLoading: false }));
      const container = document.getElementById('vc-captcha-container');
      if (container && typeof grecaptcha !== 'undefined' && !captchaRendered.current) {
        captchaRendered.current = true;
        grecaptcha.render('vc-captcha-container', {
          sitekey: regConfig.recaptchaSiteKey,
          callback: () => setCaptchaState(prev => ({ ...prev, isSolved: true })),
          'expired-callback': () => setCaptchaState(prev => ({ ...prev, isSolved: false })),
        });
      }
    }
    window.onRecaptchaLoadReact = renderCaptcha;
    if (window.recaptchaReady) renderCaptcha();
    return () => { window.onRecaptchaLoadReact = null; };
  }, []);

  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const input = document.getElementById('vc-user-timezone');
      if (input && tz) input.value = tz;
    } catch {}
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (typeof $ !== 'undefined') {
      const form = e.target;
      $(form).parsley().validate();
      if ($(form).parsley().isValid()) {
        // Inject selected portfolio companies as JSON before submitting
        if (selectedCompanies && selectedCompanies.length > 0) {
          const portfolioInput = document.getElementById('vc-intel-portfolio');
          if (portfolioInput) {
            portfolioInput.value = JSON.stringify(selectedCompanies);
          }
        }
        form.submit();
      }
    }
  };

  return (
    <div className="animate-reveal">
      <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-6">
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
            <i className="ph-fill ph-user-plus text-xl text-purple-600"></i>
          </div>
          <h3 className="text-lg font-bold text-zinc-900">Create your account while we scan</h3>
          <p className="text-sm text-zinc-500 mt-1">Your portfolio feed will be created after email verification</p>
        </div>

        <form
          id="vc-signup-form"
          method="POST"
          action="/auth/register"
          onSubmit={handleSubmit}
          data-parsley-validate
        >
          {/* Email */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 mb-1.5" htmlFor="vc-email">Work Email</label>
            <input
              type="email"
              className="w-full px-4 py-2.5 text-sm rounded-xl border border-zinc-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              id="vc-email"
              name="email"
              placeholder="you@company.com"
              value={formData.email}
              onChange={handleInputChange}
              required
              data-parsley-type="email"
              data-parsley-trigger="change"
            />
            {domainValidation.isValidating && (
              <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
                <i className="ph ph-spinner animate-spin"></i>Checking {domainValidation.domain}&hellip;
              </p>
            )}
            {domainValidation.isValid === true && (
              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <i className="ph-fill ph-check-circle"></i>Website accessible at {domainValidation.domain}
              </p>
            )}
            {domainValidation.isValid === false && (
              <p className="text-xs text-amber-600 mt-1">Please use a work email from a domain with an active website</p>
            )}
          </div>

          {/* Password */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 mb-1.5" htmlFor="vc-password">Password</label>
            <input
              type="password"
              className="w-full px-4 py-2.5 text-sm rounded-xl border border-zinc-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              id="vc-password"
              name="password"
              placeholder="Create a password"
              value={formData.password}
              onChange={handleInputChange}
              required
              data-parsley-trigger="change"
            />
          </div>

          {/* Confirm Password */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 mb-1.5" htmlFor="vc-confirm-password">Confirm Password</label>
            <input
              type="password"
              className="w-full px-4 py-2.5 text-sm rounded-xl border border-zinc-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              id="vc-confirm-password"
              name="confirmPassword"
              placeholder="Re-enter your password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              data-parsley-equalto="#vc-password"
              data-parsley-trigger="change"
            />
          </div>

          {/* Hidden fields */}
          <input type="hidden" name="referralSource" value="vc-intel-outreach" />
          <input type="hidden" name="userTimezone" id="vc-user-timezone" />
          <input type="hidden" name="termsAgree" value="on" />
          <input type="hidden" name="vc_intel_portfolio" id="vc-intel-portfolio" value="" />
          <input type="hidden" name="vc_intel_token" value={vc_token || ''} />

          {/* Captcha */}
          <div className="mb-4 flex justify-center">
            {captchaState.isLoading && (
              <div className="flex items-center justify-center h-[78px]">
                <i className="ph ph-spinner animate-spin text-zinc-400 text-xl mr-2"></i>
                <span className="text-xs text-zinc-400">Loading captcha...</span>
              </div>
            )}
            <div id="vc-captcha-container" className={captchaState.isLoading ? 'hidden' : ''}></div>
          </div>

          {/* Terms */}
          <div className="mb-5">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="termsAgree"
                checked={formData.termsAgree}
                onChange={handleInputChange}
                required
                className="mt-0.5 w-4 h-4 rounded border-zinc-300 text-purple-600 focus:ring-purple-500"
                data-parsley-trigger="change"
              />
              <span className="text-xs text-zinc-600">
                I agree to the <a href={regConfig.termsOfUseUrl} className="underline text-purple-600" target="_blank" rel="noopener noreferrer">Terms of Use</a>
              </span>
            </label>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={!captchaState.isSolved}
            className={`w-full py-3 text-sm font-semibold rounded-xl transition-colors
              ${captchaState.isSolved
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-zinc-200 text-zinc-400 cursor-not-allowed'
              }`}
          >
            Create Account &amp; Activate Feed
          </button>

          <p className="text-xs text-zinc-400 text-center mt-3">
            Already have an account? <a href="/auth/login" className="underline text-purple-600">Sign in</a>
          </p>
        </form>
      </div>
    </div>
  );
};

// ==================== Authenticated Create Feed ====================

const AuthenticatedCreateFeed = ({ selectedCompanies }) => {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const handleCreateFeed = async () => {
    if (!selectedCompanies || selectedCompanies.length === 0) {
      setError('Please select at least one company');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const res = await fetch('/vc-intel/create-feed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companies: selectedCompanies,
          vc_token: vc_token || '',
        }),
      });
      const data = await res.json();
      if (data.success) {
        alert('Portfolio Intelligence feed created!');
        window.location.href = data.redirect_url || '/dashboard/monitor';
      } else {
        setError(data.error || 'Failed to create feed');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="animate-reveal">
      <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-6">
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
            <i className="ph-fill ph-check-circle text-xl text-green-600"></i>
          </div>
          <h3 className="text-lg font-bold text-zinc-900">You&apos;re already signed in</h3>
          <p className="text-sm text-zinc-500 mt-1">
            {selectedCompanies.length > 0
              ? `Create a monitoring feed for ${selectedCompanies.length} ${selectedCompanies.length === 1 ? 'company' : 'companies'}`
              : 'Select companies from the portfolio to create a feed'}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
            <i className="ph-fill ph-warning-circle mr-1"></i>{error}
          </div>
        )}

        <button
          onClick={handleCreateFeed}
          disabled={creating || selectedCompanies.length === 0}
          className={`w-full py-3 text-sm font-semibold rounded-xl transition-colors
            ${creating || selectedCompanies.length === 0
              ? 'bg-zinc-200 text-zinc-400 cursor-not-allowed'
              : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
        >
          {creating ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ph ph-spinner animate-spin"></i>Creating Feed...
            </span>
          ) : (
            'Create Portfolio Intelligence Feed'
          )}
        </button>
      </div>
    </div>
  );
};

// ==================== Activated Section (Step 2) ====================

const ActivatedSection = ({ crawlDomain, crawlEmail, crawlCaptcha }) => {
  // For token flow: skip scan animation and load portfolio immediately
  const hasPreloadedData = HAS_TOKEN && HAS_PRELOADED;
  const [scanDone, setScanDone] = useState(hasPreloadedData);
  const [companies, setCompanies] = useState(hasPreloadedData ? [...PRELOADED_PORTFOLIO] : []);
  const [selected, setSelected] = useState(hasPreloadedData ? new Set(PRELOADED_PORTFOLIO.map(c => c.domain).filter(Boolean)) : new Set());
  const [crawlError, setCrawlError] = useState('');
  const [isCrawling, setIsCrawling] = useState(false);
  const [reachabilityStatus, setReachabilityStatus] = useState({}); // domain -> 'checking' | 'reachable'
  const crawlStarted = useRef(false);
  const reachabilityStarted = useRef(false);

  // For no-token flow: start live crawl when component mounts
  useEffect(() => {
    if (!HAS_TOKEN && crawlDomain && !crawlStarted.current) {
      crawlStarted.current = true;
      setIsCrawling(true);
      fetch('/vc-intel/crawl-portfolio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: crawlDomain, email: crawlEmail, 'g-recaptcha-response': crawlCaptcha }),
      })
        .then(res => res.json())
        .then(data => {
          setIsCrawling(false);
          if (data.error) {
            setCrawlError(data.error);
            return;
          }
          const crawled = data.companies || [];
          setCompanies(crawled);
          setSelected(new Set(crawled.map(c => c.domain).filter(Boolean)));
          setScanDone(true);
        })
        .catch(() => {
          setIsCrawling(false);
          setCrawlError('Failed to crawl portfolio. Please try again.');
        });
    }
  }, [crawlDomain]);

  // Check reachability of all companies after scan completes
  useEffect(() => {
    if (!scanDone || companies.length === 0 || reachabilityStarted.current) return;
    reachabilityStarted.current = true;

    // Mark all as checking
    const initialStatus = {};
    companies.forEach(c => { initialStatus[c.domain] = 'checking'; });
    setReachabilityStatus(initialStatus);

    // Check each company in parallel (skip companies with no domain)
    companies.filter(c => c.domain).forEach(async (company) => {
      try {
        const res = await fetch('/vc-intel/check-reachability', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ domain: company.domain }),
        });
        const data = await res.json();
        if (data.reachable) {
          setReachabilityStatus(prev => ({ ...prev, [company.domain]: 'reachable' }));
        } else {
          // Remove unreachable companies
          setCompanies(prev => prev.filter(c => c.domain !== company.domain));
          setSelected(prev => {
            const next = new Set(prev);
            next.delete(company.domain);
            return next;
          });
          setReachabilityStatus(prev => {
            const next = { ...prev };
            delete next[company.domain];
            return next;
          });
        }
      } catch {
        // On error, keep the company but mark as reachable (benefit of the doubt)
        setReachabilityStatus(prev => ({ ...prev, [company.domain]: 'reachable' }));
      }
    });
  }, [scanDone]);

  const handleScanComplete = () => {
    // For token flow: use pre-loaded portfolio data
    const initialCompanies = HAS_PRELOADED ? [...PRELOADED_PORTFOLIO] : [];
    setCompanies(initialCompanies);
    setSelected(new Set(initialCompanies.map(c => c.domain).filter(Boolean)));
    setScanDone(true);
  };

  const toggleCompany = (domain) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(domain) ? next.delete(domain) : next.add(domain);
      return next;
    });
  };

  const removeCompany = (domain) => {
    setCompanies(prev => prev.filter(c => c.domain !== domain));
    setSelected(prev => {
      const next = new Set(prev);
      next.delete(domain);
      return next;
    });
  };

  const addCompany = async (domain) => {
    if (companies.some(c => c.domain === domain)) return 'duplicate';
    // Check reachability before adding
    try {
      const res = await fetch('/vc-intel/check-reachability', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain }),
      });
      const data = await res.json();
      if (!data.reachable) {
        return 'unreachable';
      }
    } catch {
      return 'unreachable';
    }
    const newCompany = { name: domainToName(domain), domain };
    setCompanies(prev => [...prev, newCompany]);
    setSelected(prev => new Set([...prev, domain]));
    return 'ok';
  };

  const selectAll = () => setSelected(new Set(companies.map(c => c.domain)));
  const deselectAll = () => setSelected(new Set());

  // Build the list of selected company objects for form submission
  const selectedCompaniesForForm = companies.filter(c => selected.has(c.domain));

  // For token flow with pre-loaded data, decide if we show scanning animation
  const showScan = HAS_TOKEN ? !scanDone : (!scanDone && !crawlError);
  const displayDomain = crawlDomain || firm_domain;
  const displayName = HAS_TOKEN ? firm_name : (crawlDomain || 'Your Firm');

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-16 animate-reveal">
      {HAS_TOKEN && <UrgencyBanner spotsRemaining={spots_remaining} />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left column: Scan → Portfolio */}
        <div>
          <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
            {showScan ? (
              HAS_TOKEN ? (
                <ScanningStep onComplete={handleScanComplete} portfolioCount={HAS_PRELOADED ? PRELOADED_PORTFOLIO.length : 0} />
              ) : (
                /* No-token: live crawl in progress */
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <div className="relative w-20 h-20 mb-6">
                    <div className="absolute inset-0 rounded-full bg-purple-100 animate-ping opacity-30"></div>
                    <div className="absolute inset-2 rounded-full bg-purple-50 animate-pulse"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${displayDomain}&sz=64`}
                        className="w-10 h-10 rounded-xl shadow-sm bg-white p-1"
                        alt={displayName}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-zinc-900 mb-1 text-center">
                    Scanning <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">{displayName}</span>&hellip;
                  </h3>
                  <p className="text-zinc-500 text-xs mb-2">Searching for portfolio companies on the web</p>
                  <p className="text-zinc-400 text-xs">This may take 15-30 seconds</p>
                </div>
              )
            ) : (
              <div className="p-6">
                {crawlError && (
                  <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                    <i className="ph-fill ph-warning-circle mr-1"></i>{crawlError}
                  </div>
                )}

                {/* Firm header */}
                <div className="flex items-center gap-3 mb-5">
                  <img
                    src={`https://www.google.com/s2/favicons?domain=${displayDomain}&sz=64`}
                    className="w-10 h-10 rounded-xl shadow-sm bg-white p-0.5 border border-zinc-200"
                    alt={displayName}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div>
                    <h3 className="text-base font-bold text-zinc-900">{displayName}&apos;s Portfolio</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      {companies.length > 0 && (
                        <span className="text-xs text-zinc-500">{companies.length} companies discovered</span>
                      )}
                      {scanDone && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                          <i className="ph-fill ph-check-circle"></i>Scan complete
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <PortfolioGrid
                  companies={companies}
                  selected={selected}
                  onToggle={toggleCompany}
                  onRemove={removeCompany}
                  onAdd={addCompany}
                  onSelectAll={selectAll}
                  onDeselectAll={deselectAll}
                  reachabilityStatus={reachabilityStatus}
                />
              </div>
            )}
          </div>
        </div>

        {/* Right column: Signup form or Create Feed (authenticated) */}
        <div>
          {is_authenticated ? (
            <AuthenticatedCreateFeed selectedCompanies={selectedCompaniesForForm} />
          ) : (
            <InlineSignupForm selectedCompanies={selectedCompaniesForForm} />
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== Root Page ====================

const VCIntelPage = () => {
  const [state, setState] = useState('landing');
  const [crawlDomain, setCrawlDomain] = useState('');
  const [crawlEmail, setCrawlEmail] = useState('');
  const [crawlCaptcha, setCrawlCaptcha] = useState('');
  const activatedRef = useRef(null);

  const handleActivate = () => {
    setState('activated');
    setTimeout(() => {
      activatedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleActivateWithEmail = (email, domain, captchaResponse) => {
    setCrawlEmail(email);
    setCrawlDomain(domain);
    setCrawlCaptcha(captchaResponse || '');
    setState('activated');
    setTimeout(() => {
      activatedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  if (token_already_used) {
    return (
      <div className="min-h-[60vh] bg-zinc-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center px-6 py-16">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-zinc-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v.01M12 9v3m-7.071 4.071a10 10 0 1114.142 0" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-zinc-800 mb-2">This invitation has already been used</h2>
          <p className="text-zinc-500 mb-6">
            This link was already used to set up a Portfolio Intelligence feed for {firm_name}.
            If you already have an account, you can log in to view your feed.
          </p>
          <a
            href="/auth/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-800 text-white rounded-lg font-medium hover:bg-zinc-700 transition-colors"
          >
            Log in to your account
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] bg-zinc-50">
      {/* Personalization pill */}
      {HAS_TOKEN && (
        <div className="flex justify-center pt-6 pb-2">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-sm font-medium">
            <i className="ph-fill ph-seal-check"></i>
            Prepared for {firm_name}
          </div>
        </div>
      )}

      {state === 'landing' && (
        <LandingSellSection onActivate={handleActivate} onActivateWithEmail={handleActivateWithEmail} />
      )}

      {state === 'activated' && (
        <div ref={activatedRef}>
          <ActivatedSection crawlDomain={crawlDomain} crawlEmail={crawlEmail} crawlCaptcha={crawlCaptcha} />
        </div>
      )}
    </div>
  );
};

// ==================== Mount ====================

const vcIntelRoot = document.getElementById('vc-intel-root');
if (vcIntelRoot) {
  ReactDOM.render(React.createElement(VCIntelPage), vcIntelRoot);
}
